import { useEffect, useRef, useState } from "react";
import { makeNotifs } from "@utils/makeNotifs";
import { ITranslitingTooltipRef } from "../../../global/new/TranslitingTooltip/TranslitingTooltip.props";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import selectColor from "@utils/selectColor";
import { SearchPart } from "./Search.props";
import useLocalStore from "@utils/hooks/useLocalStore";
import { sub, unsub } from "@utils/pubsub";

const NUM_COLORS = 10;
export const MULTISEARCH_DIVIDER = ",";
export const SET_SEARCH_CONTROL_VALUE = "set-search-control-value";

interface SearchControlI {
  readonly search: string;
  readonly hasFocus: boolean;
  readonly ruString?: string;
  readonly parts: string[];
  readonly colors: string[];
  readonly inputRef: React.MutableRefObject<HTMLInputElement | null>;

  setSearch(s: string): void;
  onSubmit(): void;
  onClearSearch(): void;
  getTooltipRef(ref: ITranslitingTooltipRef): void;
  focus(v?: boolean): void;
}

const compileString = (srt: string) =>
  srt.split(" ").filter((item) => item.length > 0);
const prepareSearchString = (
  search: string
): [string[], string | undefined] => {
  let ruStringError: string | undefined = search.replaceAll(
    /([а-яА-ЯёЁ])/g,
    "<i>$1</i>"
  );

  if (!ruStringError.includes("<i>")) {
    ruStringError = undefined;
  }

  return [compileString(search), ruStringError];
};

export const useSearchControl = (
  storageKey: string,
  save?: (s: string[] | SearchPart[]) => void,
  multicolor?: boolean
): SearchControlI => {
  const notifs = makeNotifs();
  const toolTipRef = useRef<ITranslitingTooltipRef>();
  const { onError } = useGlobalNotifs();
  const localStore = useLocalStore<SearchPart[]>(storageKey, []);
  const storedSearch = localStore.load();
  //нельзя передавать сюда поиск через useMTFilters().values.search:
  //const MT_search = useMTFilters().values.search || [];
  // т.к. это значение устанавливаются отсюда и получается зацикливание
  const [parts, setParts] = useState<string[]>(
    storedSearch.map((s) => s.search.join(" "))
  );
  const [search, setSearch_] = useState<string>(
    parts.join(`${MULTISEARCH_DIVIDER} `)
  );
  const [ruString, setRuString] = useState<string | undefined>();
  const [hasFocus, setFocus] = useState(false);
  const [colors, setColors] = useState<string[]>(
    storedSearch.map((s) => s.color)
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const submitRef = useRef<() => void>(() => { });

  const setSearch = (x: Parameters<typeof setSearch_>[0]) => {
    return setSearch_(x);
  };

  const onChangeSearch = (data: string[]) => setSearch(data.join(" "));

  useEffect(() => {
    const cb = sub<string[]>(SET_SEARCH_CONTROL_VALUE, onChangeSearch);
    return () => unsub(SET_SEARCH_CONTROL_VALUE, cb);
  }, [onChangeSearch]);

  /*useEffect(() => {
    setSearch(MT_search.join(" ")); //-- нельзя передавать сюда поиск через useMTFilters().values.search, 
    //т.к. это значение устанавливаются отсюда и получается зацикливание
  }, [MT_search]);*/

  const getTooltipRef = (ref: ITranslitingTooltipRef) => {
    toolTipRef.current = ref;
  };

  const saveSearch = (s: SearchPart[]) => {
    //localStore.store(s);
    save && save(s);
  };

  /*useEffect(() => {
    const s = localStore.load();
    saveSearch(s);
  }, []);*/

  submitRef.current = () => {
    if (search.length >= 1 && search.length < 3)
      return onError(notifs.errors.search_length);

    if (multicolor) {
      const searchArgs: SearchPart[] = [];
      let ruStringError: string | undefined = undefined;
      for (let i = 0; i < parts.length; i++) {
        const [s, rse] = prepareSearchString(parts[i]);
        if (ruStringError === undefined && rse !== undefined)
          ruStringError = rse;
        searchArgs.push({ search: s, color: colors[i] });
      }
      setRuString(ruStringError);
      saveSearch(searchArgs);
    } else {
      const [s, ruStringError] = prepareSearchString(search);
      setRuString(ruStringError);
      save && save(s);
    }
  };

  const onClearSearch = () => {
    setSearch("");
    saveSearch([]);
  };

  useEffect(() => {
    const pts = search
      .split(`${MULTISEARCH_DIVIDER}`)
      .map((s) => s.trim())
      .filter((s) => !!s.length);
    setParts(pts);
    if (search.length >= 1 && search.length < 3) return;
    const t = setTimeout(() => submitRef.current(), 2000);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const len = Math.ceil(parts.length / NUM_COLORS) * NUM_COLORS;
    setColors(
      parts.map((_, i) => (i ? selectColor(i, len, 100, 35) : "unset"))
    );
  }, [parts]);

  // useEffect(() => {
  //   if (inputRef.current) inputRef.current.focus();
  // }, [hasFocus])

  return {
    search,
    ruString,
    hasFocus,
    parts,
    colors,
    inputRef,

    setSearch,
    onSubmit() {
      submitRef.current();
      setFocus(false);
    },
    onClearSearch,
    getTooltipRef,
    focus(v) {
      setFocus(!!v);
    },
  };
};
